import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Loader2, Paperclip, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useActiveClinic } from '@/lib/activeClinic';
import { ApiCallError } from '@/lib/api';
import {
  AUTHOR_ROLE_CODES,
  CARE_COMMUNICATION_CATEGORIES,
  patientHasIdentity,
  useCareCommunicationThread,
  useClinicCommunicateConnection,
  useSendCareCommunication,
  type CareCommunicationCategory,
  type PayloadSegmentInput,
  type SendMessageType,
} from '@/hooks/useCareCommunications';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { ErrorAlert } from '@/components/ui/ErrorAlert';

type PatientIdKind = 'cpr' | 'xEcpr' | 'dEcpr';

/** MedCom ValueSet medcom-core-attachmentMimeTypes — only these 6 are allowed. */
const ALLOWED_ATTACHMENT_MIME_TYPES = [
  'application/pdf',
  'image/gif',
  'image/jpeg',
  'image/png',
  'image/tiff',
  'image/bmp',
] as const;

type AllowedAttachmentMimeType = (typeof ALLOWED_ATTACHMENT_MIME_TYPES)[number];

interface AttachmentDraft {
  contentType: string;
  title: string;
  data?: string;
  url?: string;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Could not read file'));
        return;
      }
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('Could not read file'));
    reader.readAsDataURL(file);
  });
}

export function ComposeCareCommunication({ mode }: { mode: SendMessageType }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { communicationIdentifier: rawId } = useParams<{ communicationIdentifier?: string }>();
  const communicationIdentifier = rawId ? decodeURIComponent(rawId) : undefined;

  const { activeClinicId } = useActiveClinic();
  const send = useSendCareCommunication();
  const { data: connection } = useClinicCommunicateConnection(activeClinicId);
  const { data: threadData } = useCareCommunicationThread(
    mode === 'new' ? null : activeClinicId,
    mode === 'new' ? null : communicationIdentifier,
  );

  const [category, setCategory] = useState<CareCommunicationCategory>('other');
  const [topic, setTopic] = useState('');
  const [priority, setPriority] = useState<'routine' | 'asap' | ''>('');
  const [messageText, setMessageText] = useState('');
  const [attachments, setAttachments] = useState<AttachmentDraft[]>([]);
  const [attUrl, setAttUrl] = useState('');
  const [attUrlType, setAttUrlType] = useState<AllowedAttachmentMimeType>('application/pdf');
  const [attUrlTitle, setAttUrlTitle] = useState('');

  const [senderSorId, setSenderSorId] = useState('');
  const [senderName, setSenderName] = useState('');
  const [receiverSorId, setReceiverSorId] = useState('');
  const [receiverName, setReceiverName] = useState('');

  const [patientIdKind, setPatientIdKind] = useState<PatientIdKind>('cpr');
  const [patientIdValue, setPatientIdValue] = useState('');
  const [patientFamily, setPatientFamily] = useState('');
  const [patientGiven, setPatientGiven] = useState('');

  const [authorFamily, setAuthorFamily] = useState('');
  const [authorGiven, setAuthorGiven] = useState('');
  const [authorRoleCode, setAuthorRoleCode] = useState<string>('psykolog');
  const [authorPhone, setAuthorPhone] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [prefilled, setPrefilled] = useState(false);

  useEffect(() => {
    if (connection?.sor_id && !senderSorId) {
      setSenderSorId(connection.sor_id);
    }
  }, [connection?.sor_id, senderSorId]);

  useEffect(() => {
    if (mode === 'new' || !threadData || prefilled) return;
    const assembled = threadData.assembled;
    if (assembled?.categoryCode) {
      setCategory(assembled.categoryCode as CareCommunicationCategory);
    }
    if (assembled?.topic) setTopic(assembled.topic);
    // Prefill patient demographics only (never surface CPR into inputs from prior message).
    if (assembled?.patient) {
      if (assembled.patient.family) setPatientFamily(assembled.patient.family);
      if (assembled.patient.given?.length) setPatientGiven(assembled.patient.given.join(' '));
      // Identity: prefer kind flags without copying CPR into editable field for reply safety —
      // clinician must re-enter identifier for send (Communicate requires it).
      if (assembled.patient.xEcpr) setPatientIdKind('xEcpr');
      else if (assembled.patient.dEcpr) setPatientIdKind('dEcpr');
      else setPatientIdKind('cpr');
    }
    // Reply/forward: swap sender/receiver for reply (we become sender of prior receiver).
    const latest = threadData.messages[threadData.messages.length - 1];
    if (latest) {
      if (mode === 'reply') {
        setReceiverSorId(latest.senderSor ?? '');
        setSenderSorId(latest.receiverSor ?? connection?.sor_id ?? '');
      } else {
        // Forward: keep prior receiver empty for clinician to set destination.
        setSenderSorId(connection?.sor_id ?? latest.receiverSor ?? '');
      }
    }
    setPrefilled(true);
  }, [mode, threadData, prefilled, connection?.sor_id]);

  const titleKey =
    mode === 'reply'
      ? 'communicate.compose_reply_title'
      : mode === 'forward'
        ? 'communicate.compose_forward_title'
        : 'communicate.compose_new_title';

  const onAttach = async (files: FileList | null) => {
    if (!files?.length) return;
    try {
      const next: AttachmentDraft[] = [];
      for (const file of Array.from(files)) {
        if (
          !(ALLOWED_ATTACHMENT_MIME_TYPES as readonly string[]).includes(file.type)
        ) {
          toast.error(t('communicate.errors.attachment_type_not_allowed'));
          continue;
        }
        const data = await fileToBase64(file);
        next.push({
          contentType: file.type,
          title: file.name,
          data,
        });
      }
      if (next.length) {
        setAttachments((prev) => [...prev, ...next]);
      }
    } catch {
      toast.error(t('communicate.errors.attachment_read'));
    }
  };

  const onAddUrlAttachment = () => {
    const url = attUrl.trim();
    if (!url) {
      toast.error(t('communicate.errors.attachment_url_required'));
      return;
    }
    setAttachments((prev) => [
      ...prev,
      {
        contentType: attUrlType,
        title: attUrlTitle.trim() || url,
        url,
      },
    ]);
    setAttUrl('');
    setAttUrlType('application/pdf');
    setAttUrlTitle('');
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!activeClinicId) {
      setSubmitError(t('communicate.errors.no_clinic'));
      return;
    }
    if (!senderSorId.trim() || !senderName.trim()) {
      setSubmitError(t('communicate.errors.sender_required'));
      return;
    }
    if (!receiverSorId.trim() || !receiverName.trim()) {
      setSubmitError(t('communicate.errors.receiver_required'));
      return;
    }
    if (!patientFamily.trim() || !patientIdValue.trim()) {
      setSubmitError(t('communicate.errors.patient_required'));
      return;
    }
    if (!authorFamily.trim() || !authorGiven.trim() || !authorPhone.trim()) {
      setSubmitError(t('communicate.errors.author_required'));
      return;
    }
    if (!messageText.trim() && attachments.length === 0) {
      setSubmitError(t('communicate.errors.message_required'));
      return;
    }
    if (category === 'other' && !topic.trim()) {
      setSubmitError(t('communicate.errors.topic_required_other'));
      return;
    }
    if (mode !== 'new' && !communicationIdentifier) {
      setSubmitError(t('communicate.errors.parent_required'));
      return;
    }

    if (patientIdKind === 'cpr') {
      const digits = patientIdValue.replace(/\D/g, '');
      if (digits.length !== 10) {
        setSubmitError(t('communicate.errors.cpr_format'));
        return;
      }
    }

    const payloads: PayloadSegmentInput[] = [];
    if (messageText.trim()) {
      // Preserve linebreaks — content is sent as-is to Communicate.
      payloads.push({ kind: 'string', content: messageText });
    }
    for (const att of attachments) {
      const attachment: {
        contentType: string;
        title: string;
        data?: string;
        url?: string;
      } = { contentType: att.contentType, title: att.title };
      if (att.data) attachment.data = att.data;
      if (att.url) attachment.url = att.url;
      payloads.push({ kind: 'attachment', attachment });
    }

    const patient: Record<string, unknown> = {
      family: patientFamily.trim(),
      given: patientGiven
        .trim()
        .split(/\s+/)
        .filter(Boolean),
    };
    if (patientIdKind === 'cpr') patient.cpr = patientIdValue.replace(/\D/g, '');
    else if (patientIdKind === 'xEcpr') patient.xEcpr = patientIdValue.trim();
    else patient.dEcpr = patientIdValue.trim();

    const parentMessageId = threadData?.messages.length
      ? threadData.messages[threadData.messages.length - 1]?.id
      : undefined;

    setSubmitting(true);
    try {
      const result = await send.mutateAsync({
        clinicId: activeClinicId,
        type: mode,
        ...(mode !== 'new'
          ? {
              parent: {
                ...(parentMessageId ? { messageId: parentMessageId } : {}),
                communicationIdentifier,
              },
            }
          : {}),
        sender: {
          sorId: senderSorId.trim(),
          name: senderName.trim(),
        },
        receiver: {
          sorId: receiverSorId.trim(),
          name: receiverName.trim(),
        },
        patient: patient as {
          cpr?: string;
          xEcpr?: string;
          dEcpr?: string;
          family: string;
          given?: string[];
        },
        category,
        topic: topic.trim() || undefined,
        ...(category === 'regarding-referral' && priority
          ? { priority }
          : {}),
        author: {
          family: authorFamily.trim(),
          given: authorGiven
            .trim()
            .split(/\s+/)
            .filter(Boolean),
          roleCode: authorRoleCode,
          phone: authorPhone.trim(),
        },
        payloads,
      });

      toast.success(t('communicate.toast_sent'));
      navigate(
        `/care-communications/${encodeURIComponent(result.communicationIdentifier)}`,
      );
    } catch (err) {
      if (err instanceof ApiCallError) {
        if (err.code === 'invalid-input') {
          setSubmitError(t('communicate.errors.invalid_input'));
        } else if (err.code === 'forbidden') {
          setSubmitError(t('communicate.errors.forbidden'));
        } else {
          setSubmitError(err.message);
        }
      } else {
        setSubmitError(t('communicate.errors.unknown'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const backTo =
    mode !== 'new' && communicationIdentifier
      ? `/care-communications/${encodeURIComponent(communicationIdentifier)}`
      : '/care-communications';

  const hasPriorPatient = patientHasIdentity(threadData?.assembled?.patient);

  return (
    <div className="min-h-screen bg-page-light dark:bg-page-dark">
      <main className="py-6">
        <div className="mx-auto max-w-3xl px-6">
          <button
            type="button"
            onClick={() => navigate(backTo)}
            className="mb-4 inline-flex items-center text-sm text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
          >
            <ArrowLeft className="mr-1 h-4 w-4" />
            {t('communicate.back')}
          </button>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t(titleKey)}
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {t('communicate.compose_subtitle')}
          </p>

          {mode !== 'new' && hasPriorPatient && (
            <p className="mt-3 text-xs text-amber-700 dark:text-amber-300">
              {t('communicate.patient_identity_reenter_hint')}
            </p>
          )}

          {submitError && (
            <div className="mt-4">
              <ErrorAlert message={submitError} />
            </div>
          )}

          <form onSubmit={onSubmit} className="mt-6 space-y-8">
            <section className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                {t('communicate.section_message')}
              </h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                  {t('communicate.field_category')}
                </label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value as CareCommunicationCategory)}
                  className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                >
                  {CARE_COMMUNICATION_CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {t(`communicate.category_${c}`)}
                    </option>
                  ))}
                </select>
              </div>
              <Input
                label={t('communicate.field_topic')}
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                required={category === 'other'}
              />
              {category === 'regarding-referral' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                    {t('communicate.field_priority')}
                  </label>
                  <select
                    value={priority}
                    onChange={(e) => setPriority(e.target.value as 'routine' | 'asap' | '')}
                    className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">{t('communicate.priority_none')}</option>
                    <option value="routine">{t('communicate.priority_routine')}</option>
                    <option value="asap">{t('communicate.priority_asap')}</option>
                  </select>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                  {t('communicate.field_message')}
                </label>
                <textarea
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  rows={8}
                  className="mt-1 block w-full whitespace-pre-wrap rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  placeholder={t('communicate.field_message_placeholder')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                  {t('communicate.field_attachments')}
                </label>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <label className="inline-flex cursor-pointer items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 shadow-sm hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600">
                    <Paperclip className="mr-1 h-4 w-4" />
                    {t('communicate.add_attachment')}
                    <input
                      type="file"
                      className="hidden"
                      multiple
                      accept="application/pdf,image/gif,image/jpeg,image/png,image/tiff,image/bmp"
                      onChange={(e) => {
                        void onAttach(e.target.files);
                        e.target.value = '';
                      }}
                    />
                  </label>
                  {attachments.map((att, idx) => (
                    <span
                      key={`${att.title}-${idx}`}
                      className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700 dark:bg-gray-700 dark:text-gray-200"
                    >
                      {att.title}
                      <button
                        type="button"
                        className="ml-1 text-gray-500 hover:text-gray-800"
                        onClick={() =>
                          setAttachments((prev) => prev.filter((_, i) => i !== idx))
                        }
                        aria-label={t('communicate.remove_attachment')}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="mt-3 grid gap-3 sm:grid-cols-[1fr_auto_1fr_auto] sm:items-end">
                  <Input
                    label={t('communicate.field_attachment_url')}
                    value={attUrl}
                    onChange={(e) => setAttUrl(e.target.value)}
                  />
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                      {t('communicate.field_attachment_type')}
                    </label>
                    <select
                      value={attUrlType}
                      onChange={(e) =>
                        setAttUrlType(e.target.value as AllowedAttachmentMimeType)
                      }
                      className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    >
                      {ALLOWED_ATTACHMENT_MIME_TYPES.map((mime) => (
                        <option key={mime} value={mime}>
                          {mime}
                        </option>
                      ))}
                    </select>
                  </div>
                  <Input
                    label={t('communicate.field_attachment_title')}
                    value={attUrlTitle}
                    onChange={(e) => setAttUrlTitle(e.target.value)}
                  />
                  <Button type="button" variant="secondary" onClick={onAddUrlAttachment}>
                    {t('communicate.add_attachment_url')}
                  </Button>
                </div>
              </div>
            </section>

            <section className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                {t('communicate.section_parties')}
              </h3>
              <div className="grid gap-4 sm:grid-cols-2">
                <Input
                  label={t('communicate.field_sender_sor')}
                  value={senderSorId}
                  onChange={(e) => setSenderSorId(e.target.value)}
                  required
                />
                <Input
                  label={t('communicate.field_sender_name')}
                  value={senderName}
                  onChange={(e) => setSenderName(e.target.value)}
                  required
                />
                <Input
                  label={t('communicate.field_receiver_sor')}
                  value={receiverSorId}
                  onChange={(e) => setReceiverSorId(e.target.value)}
                  required
                />
                <Input
                  label={t('communicate.field_receiver_name')}
                  value={receiverName}
                  onChange={(e) => setReceiverName(e.target.value)}
                  required
                />
              </div>
            </section>

            <section className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                {t('communicate.section_patient')}
              </h3>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                    {t('communicate.field_patient_id_kind')}
                  </label>
                  <select
                    value={patientIdKind}
                    onChange={(e) => setPatientIdKind(e.target.value as PatientIdKind)}
                    className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="cpr">{t('communicate.id_kind_cpr')}</option>
                    <option value="xEcpr">{t('communicate.id_kind_xEcpr')}</option>
                    <option value="dEcpr">{t('communicate.id_kind_dEcpr')}</option>
                  </select>
                </div>
                <Input
                  label={t('communicate.field_patient_id')}
                  value={patientIdValue}
                  onChange={(e) => setPatientIdValue(e.target.value)}
                  autoComplete="off"
                  required
                />
                <Input
                  label={t('communicate.field_patient_family')}
                  value={patientFamily}
                  onChange={(e) => setPatientFamily(e.target.value)}
                  required
                />
                <Input
                  label={t('communicate.field_patient_given')}
                  value={patientGiven}
                  onChange={(e) => setPatientGiven(e.target.value)}
                />
              </div>
            </section>

            <section className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                {t('communicate.section_author')}
              </h3>
              <div className="grid gap-4 sm:grid-cols-2">
                <Input
                  label={t('communicate.field_author_family')}
                  value={authorFamily}
                  onChange={(e) => setAuthorFamily(e.target.value)}
                  required
                />
                <Input
                  label={t('communicate.field_author_given')}
                  value={authorGiven}
                  onChange={(e) => setAuthorGiven(e.target.value)}
                  required
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                    {t('communicate.field_author_role')}
                  </label>
                  <select
                    value={authorRoleCode}
                    onChange={(e) => setAuthorRoleCode(e.target.value)}
                    className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  >
                    {AUTHOR_ROLE_CODES.map((code) => (
                      <option key={code} value={code}>
                        {t(`communicate.role_${code}`, { defaultValue: code })}
                      </option>
                    ))}
                  </select>
                </div>
                <Input
                  label={t('communicate.field_author_phone')}
                  value={authorPhone}
                  onChange={(e) => setAuthorPhone(e.target.value)}
                  required
                />
              </div>
            </section>

            <div className="flex justify-end gap-3">
              <Button type="button" variant="secondary" onClick={() => navigate(backTo)}>
                {t('communicate.cancel')}
              </Button>
              <Button type="submit" disabled={submitting || !activeClinicId}>
                {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t('communicate.send')}
              </Button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}

export function NewCareCommunication() {
  return <ComposeCareCommunication mode="new" />;
}

export function ReplyCareCommunication() {
  return <ComposeCareCommunication mode="reply" />;
}

export function ForwardCareCommunication() {
  return <ComposeCareCommunication mode="forward" />;
}
